<template>
    <div class="room-list-wrapper">
        <div class="room-list-container">
            <div class="sidebar">
                <create-room-form />
                <div class="discord-invite">
                    <a class="discord-invite-link" href="https://discord.gg/AmeKEDc" target="_blank">
                        <span class="discord-invite-text">Join Us On </span>
                        <img class="discord-invite-btn"
                                :src="discordLogoImage" />
                    </a>
                </div>
                <div class="tip-jars">
					<tip-jar
						url="https://ko-fi.com/sprucegoose"
						title="Support SpruceGoose"
						subtitle="(site creator)"
					/>
					<tip-jar
						url="https://ko-fi.com/kingsharaman"
						title="Support manu5"
						subtitle="(AI creator)"
					/>
                </div>
            </div>
            <div class="content" v-if="!loading">
                <div>
                    <announcement
                        v-if="showAnnouncement"
                        :title="activeAnnouncement.title"
                        :content="activeAnnouncement.content"
                        @dismiss="dismissAnnouncement"
                    />
                    <div class="search-form">
                        <input
                            class="search-input"
                            name="search"
                            placeholder="Search rooms"
                            v-model="search.name"
                            @input="onSearch(search.name)"
                            autocomplete="off"
                        />
                        <span class="search-icon">
                            <fa-icon icon="search" />
                        </span>
                    </div>
                    <div class="room-list">
                        <room
                            :room="room"
                            v-for="(room) in rooms"
                            :key="`room-${room.id}`"
                            :refreshTime="refreshTime"
                        />
                        <div class="no-matches" v-if="!loading && !rooms.length">
                            {{ search.name ? 'No matching rooms found.' : 'No games in progress.' }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="content placeholder" v-if="loading">
                <div class="room-list-placeholder">
                    Loading rooms<span class="ellipsis animated-ellipsis"></span>
                </div>
            </div>
        </div>
        <password-modal v-if="passwordModal" :options="passwordModal" />
    </div>
</template>

<script>
import throttle from 'lodash.throttle';
import moment from 'moment';
import discordLogoImage from '../../assets/discord_logo.svg?url';
import api from '../../helpers/api';
import { isMobileDevice } from '../../helpers/general';
import { MOBILE_MAX_WIDTH } from '../../App-types';
import CreateRoomForm from '../CreateRoomForm/CreateRoomForm';
import Announcement from '../Announcement/Announcement';

export default {
    name: 'RoomList',
    components: {
        Announcement,
        CreateRoomForm,
    },
    async created() {
        const interval = 60 * 1000;
        await this.$store.dispatch('getRooms');
        this.loading = false;

        clearInterval(this.refreshInterval);

        this.refreshInterval = setInterval(() => {
            this.refreshTime = moment();
        }, interval);
    },
    async mounted() {
        await this.getAnnouncement();
    },
    beforeUnmount() {
        clearInterval(this.refreshInterval);
    },
    data() {
        return {
            activeAnnouncement: null,
            discordLogoImage,
            search: {
                name: '',
            },
            loading: true,
            refreshInterval: null,
            refreshTime: null,
        };
    },
    computed: {
        dismissedAnnouncementIds() {
            return this.$store.state.appState.dismissedAnnouncementIds || [];
        },
        showAnnouncement() {
            return !!this.activeAnnouncement &&
                !this.dismissedAnnouncementIds.includes(this.activeAnnouncement.id);
        },
        rooms() {
            return this.$store.state.roomListState.rooms;
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        passwordModal() {
            return this.$store.state.roomListState.passwordModal;
        },
    },
    methods: {
        async getAnnouncement() {
            const response = await api.request('/announcement', 'GET', null, false);

            if (response.ok) {
				try {
                	this.activeAnnouncement = await response.json();
				} catch {
					this.activeAnnouncement = null
				}
            }
        },
        dismissAnnouncement() {
            if (!this.activeAnnouncement) {
                return;
            }

            this.$store.dispatch('dismissAnnouncement', this.activeAnnouncement.id);
        },
        isMobile() {
            return window.innerWidth <= MOBILE_MAX_WIDTH || isMobileDevice();
        },
        onSearch: throttle(async function onSearch(name) {
            this.$store.dispatch('getRooms', name);
        }, 1000),
    },
    sockets: {
        connect() {
            setTimeout(() => {
                if (this.isMobile()) {
                    this.$store.dispatch('getRooms');
                }
            }, 0);
        },
        onRoomsUpdated(rooms) {
            this.$store.dispatch('setRooms', rooms);
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .room-list-container {
        text-align: center;
        display: flex;
        flex-direction: column;

        .sidebar {
            padding: 25px 20px 0 20px;
        }

        .content {
            flex-grow: 1;
            padding: 25px;

            &.placeholder {
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            .search-form {
                margin-bottom: 25px;
                position: relative;
                width: calc(100% - 25px);

                .search-input {
                    width: 100%;
                    background: transparent;
                    border: 0;
                    border-bottom: 1px solid $dark-blue;
                    font-size: 1.1em;
                    padding-bottom: 5px;
                    padding-left: 25px;

                    &::placeholder {
                        color: $dark-blue;
                    }
                }

                .search-icon {
                    position: absolute;
                    top: 0;
                    left: 0;
                }
            }

            .room-list {
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                position: relative;
            }
        }

        .discord-invite {
            margin: 15px auto 0;

            .discord-invite-link {
                color: $discord;
                text-decoration: none;

                &:hover,
                &:active,
                &:focus,
                &:visited {
                    color: $discord;
                    text-decoration: none;
                }
            }

            .discord-invite-text {
                color: $discord;
                font-size: 1.25em;
                text-transform: uppercase;
                font-family: 'Discord', 'Avenir', 'Arial';
                font-weight: bold;
                vertical-align: middle;
            }

            .discord-invite-btn {
                height: auto;
                max-width: 125px;
                max-height: 75px;
                width: auto;
                vertical-align: middle;
            }
        }
    }

    .tip-jars {
        display: flex;
        align-items: stretch;
        justify-content: center;
        gap: 2px;
		margin-top: 10px;
    }

    @media only screen and (min-width: $screen-xs) {
        .tip-jars {
            flex-direction: column;
			gap: 10px;
            width: fit-content;
            max-width: 100%;
            margin-left: auto;
            margin-right: auto;
        }

        .room-list-container {

            .content {
            }

            .mobile-optimization-wrapper {
                display: flex;
            }
        }
    }

    @media only screen and (min-width: $screen-md) {

        .room-list-container {
            flex-direction: row;

            .sidebar {
                padding: 25px;
            }
        }

    }

</style>
